import { getSignatures } from '@api/lambda/signatures';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { SORT_VALUES } from '@features/DocViewer/Steps/Docs/constants';
import { SIGNATURES_PAGE_LIMIT } from '@shared/constants';
import { useSignaturesPaginationContext } from 'context/SignaturesPaginationContext';

const useGetPaginatedSignatures = (userId, _options) => {
  const [sort, setSort] = useState(SORT_VALUES.desc);
  const { page, setPage } = useSignaturesPaginationContext();

  const { asc, desc } = SORT_VALUES;

  const queryKey = useMemo(() => ['signatures', userId, sort], [userId, sort]);

  const limit = SIGNATURES_PAGE_LIMIT;

  const { data, hasNextPage, fetchNextPage, isLoading } = useInfiniteQuery(
    queryKey,
    ({ pageParam = undefined }) => {
      return getSignatures(userId, {
        nextPk: pageParam?.pk,
        nextSk: pageParam?.sk,
        limit,
      });
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.next;
      },
    },
  );

  const fetchPage = useCallback(
    async newPage => {
      let fetchedPage = page - 1;
      if (fetchedPage === 0) {
        fetchedPage++;
      }
      while (data?.pageParams[fetchedPage] && fetchedPage < newPage) {
        fetchedPage++;
      }

      while (fetchedPage < newPage) {
        await fetchNextPage();
        fetchedPage++;
      }
    },
    [page, fetchNextPage, data?.pageParams],
  );

  const changeSort = () => {
    setPage(1);
    setSort(prev => (prev === asc ? desc : asc));
  };

  return {
    data: data?.pages[(page || 1) - 1],
    changeSort,
    sort,
    hasPrevious: page > 1,
    hasNext: hasNextPage,
    fetchPage,
    count: data?.total,
    isLoading,
  };
};

export default useGetPaginatedSignatures;
